'use client';

import React, { useState, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { HiOutlineSearch } from '@react-icons/all-files/hi/HiOutlineSearch';
import {
  StyledToolHero,
  StyledToolBody,
  StyledCalculator,
  StyledFormGroup,
  StyledCalculateButton,
  StyledResult,
  StyledStatsGrid,
  StyledStatCard,
  StyledWidgetsSection,
  StyledWidgetGrid,
  StyledWidgetCard,
  StyledRelatedTools,
  StyledRelatedToolGrid,
  StyledRelatedToolCard,
} from './WordCounterTool.styles';

interface IWidget {
  _id: string;
  name: string;
  slug: string;
  logo: string;
  teaser: string;
}

interface IRelatedTool {
  slug: string;
  name: string;
  description: string;
}

interface IToolResult {
  words: number;
  charsWithSpaces: number;
  charsWithoutSpaces: number;
  sentences: number;
  paragraphs: number;
  readingTime: string;
  speakingTime: string;
  status: 'good';
}

interface IProps {
  recommendedWidgets: IWidget[];
  relatedTools: IRelatedTool[];
}

function formatTime(minutes: number): string {
  if (minutes < 1) return 'less than 1 min';
  const mins = Math.round(minutes);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remaining = mins % 60;
  return remaining === 0 ? `${hours} hr` : `${hours} hr ${remaining} min`;
}

function analyzeText(text: string): IToolResult {
  const words = text.trim() === '' ? 0 : text.trim().split(/\s+/).filter(Boolean).length;
  const charsWithSpaces = text.length;
  const charsWithoutSpaces = text.replace(/\s/g, '').length;
  const sentences = (text.match(/[.!?]+(\s|$)/g) || []).length;
  const paragraphs = text.trim() === '' ? 0 : text.split(/\n\s*\n/).filter((p) => p.trim().length > 0).length;

  const readingMinutes = words / 200;
  const speakingMinutes = words / 130;

  return {
    words,
    charsWithSpaces,
    charsWithoutSpaces,
    sentences: Math.max(sentences, words > 0 ? 1 : 0),
    paragraphs: Math.max(paragraphs, words > 0 ? 1 : 0),
    readingTime: formatTime(readingMinutes),
    speakingTime: formatTime(speakingMinutes),
    status: 'good',
  };
}

function getWidgetDescription(slug: string, result: IToolResult): string {
  switch (slug) {
    case 'accordion':
      return `With ${result.words.toLocaleString()} words, organize your content into accordion sections. Keep pages scannable while giving search engines all the keyword-rich text they need.`;
    case 'tabs':
      return `Tabs let you present ${result.words.toLocaleString()} words of content in organized, topic-specific sections without overwhelming your readers on the first scroll.`;
    case 'comparison-tables':
      return 'Comparison tables convert dense feature content into scannable rows. A great complement to long-form pages with high word counts.';
    case 'feeds':
      return 'Dynamic content feeds keep your pages fresh. New content means new words, new keywords, and better crawl frequency from search engines.';
    default:
      return 'Improve your content presentation and SEO with powerful widgets.';
  }
}

function getWidgetCTA(slug: string): string {
  switch (slug) {
    case 'accordion': return 'Add Accordion Free →';
    case 'tabs': return 'Add Tabs Free →';
    case 'comparison-tables': return 'Add Tables Free →';
    case 'feeds': return 'Add Feeds Free →';
    default: return 'Try It Free →';
  }
}

export const WordCounterTool = ({ recommendedWidgets, relatedTools }: IProps) => {
  const [content, setContent] = useState('');
  const [result, setResult] = useState<IToolResult | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [error, setError] = useState('');
  const resultRef = useRef<HTMLDivElement>(null);

  const handleCount = () => {
    const trimmed = content.trim();
    if (!trimmed) {
      setError('Please paste some text to analyze.');
      return;
    }
    setError('');

    const analysis = analyzeText(content);
    setResult(analysis);
    setHasCalculated(true);
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  return (
    <>
      <StyledToolHero>
        <h1>Free <span className="highlight">Word Counter</span></h1>
        <p>
          Paste any text and instantly count words, characters, sentences, paragraphs,
          and get reading and speaking time estimates. Free, no sign-up required.
        </p>
      </StyledToolHero>

      <StyledToolBody>
        <StyledCalculator>
          <h2>Count Your Words</h2>

          <StyledFormGroup>
            <label htmlFor="content">Your Text</label>
            <textarea
              id="content"
              placeholder="Paste your article, blog post, script, or any text here..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </StyledFormGroup>

          {error && (
            <p style={{ color: '#e22029', fontSize: '0.875rem', marginBottom: '16px' }}>{error}</p>
          )}

          <StyledCalculateButton onClick={handleCount}>
            Count Words
          </StyledCalculateButton>
        </StyledCalculator>

        {hasCalculated && result && (
          <>
            <StyledResult status={result.status} ref={resultRef}>
              <p className="result-label">Text Analysis</p>
              <StyledStatsGrid>
                <StyledStatCard accent="blue">
                  <span className="stat-value">{result.words.toLocaleString()}</span>
                  <span className="stat-label">Words</span>
                </StyledStatCard>
                <StyledStatCard accent="purple">
                  <span className="stat-value">{result.charsWithSpaces.toLocaleString()}</span>
                  <span className="stat-label">Characters (with spaces)</span>
                </StyledStatCard>
                <StyledStatCard accent="gray">
                  <span className="stat-value">{result.charsWithoutSpaces.toLocaleString()}</span>
                  <span className="stat-label">Characters (no spaces)</span>
                </StyledStatCard>
                <StyledStatCard accent="green">
                  <span className="stat-value">{result.sentences.toLocaleString()}</span>
                  <span className="stat-label">Sentences</span>
                </StyledStatCard>
                <StyledStatCard accent="orange">
                  <span className="stat-value">{result.paragraphs.toLocaleString()}</span>
                  <span className="stat-label">Paragraphs</span>
                </StyledStatCard>
                <StyledStatCard accent="teal">
                  <span className="stat-value">{result.readingTime}</span>
                  <span className="stat-label">Reading time (200 wpm)</span>
                </StyledStatCard>
                <StyledStatCard accent="pink">
                  <span className="stat-value">{result.speakingTime}</span>
                  <span className="stat-label">Speaking time (130 wpm)</span>
                </StyledStatCard>
              </StyledStatsGrid>

              <div className="result-pain">
                <strong>Content length guide:</strong> Blog posts typically perform best at
                1,500 to 2,500 words. Pillar content often exceeds 3,000. Landing pages convert
                well at 500 to 1,500. Product pages rank with 300 to 800. Match your word count
                to search intent and what top competitors are publishing.
              </div>
            </StyledResult>

            <StyledWidgetsSection>
              <h2>Make Your Words Work Harder</h2>
              <p className="subtitle">
                Structure your content for better engagement and search rankings with these free widgets.
              </p>
              <StyledWidgetGrid>
                {recommendedWidgets.map((widget) => (
                  <StyledWidgetCard
                    key={widget._id}
                    href={`/widgets/${widget.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="widget-header">
                      {widget.logo && (
                        <Image
                          src={widget.logo}
                          alt={`${widget.name} logo`}
                          width={48}
                          height={48}
                        />
                      )}
                      <h3>{widget.name}</h3>
                    </div>
                    <p>{getWidgetDescription(widget.slug, result)}</p>
                    <span className="widget-cta">{getWidgetCTA(widget.slug)}</span>
                  </StyledWidgetCard>
                ))}
              </StyledWidgetGrid>
            </StyledWidgetsSection>
          </>
        )}

        {relatedTools.length > 0 && (
          <StyledRelatedTools>
            <h2>Explore More Free SEO Tools</h2>
            <p className="subtitle">
              More free tools to optimize and measure your content.
            </p>
            <StyledRelatedToolGrid>
              {relatedTools.map((tool) => (
                <StyledRelatedToolCard
                  key={tool.slug}
                  as={Link}
                  href={`/free-tools/seo/${tool.slug}`}
                >
                  <span className="tool-icon"><HiOutlineSearch /></span>
                  <h3>{tool.name}</h3>
                  <p>{tool.description}</p>
                  <span className="tool-cta">Use Tool →</span>
                </StyledRelatedToolCard>
              ))}
            </StyledRelatedToolGrid>
          </StyledRelatedTools>
        )}
      </StyledToolBody>
    </>
  );
};
